import { ApiService } from '@classes/apiClasses';
import { ICasinoInfo, ILeaderBoardList, IListResponse, ISlotResponse } from '@interfaces/services';
import { FloorPlan, SlotMetaData } from 'appRedux/createStore';
import {
  ICasinoSchema,
  ICasinoReport,
  ICasinoReportCount,
  ISearchInfo,
  ILeaderBoardReport,
  ILeaderBoardChallengeReport,
  IGraphGameData,
  ITrendsReport,
} from 'appRedux/models/casinoModels';
import { OrderByEnum, OrderValueEnum } from 'common/common';
import { clientLocalNowAsUtcMs } from 'common/graphBuckets';
import { IGameData } from 'components/CasinoTabBar/views/home/views/MapStats/HotListReport/components/CompareAdvancedView/types';
import { Get, Post } from 'lib/axiosWrapper';
import { number } from 'prop-types';
import { Observable, forkJoin, of } from 'rxjs';
import { resolveInsightTimeframe } from 'config/insightTimeframes';
import { catchError, map } from 'rxjs/operators';
import {
  asReportRows,
  emptyList,
  listResponse,
  slotResponse,
  insightCasinoId,
  IInsightSummaryResponse,
  isInsightCasino,
  pickInsightSummaryRow,
  parseSlotList,
  sharedInsightSummary,
  sortRowsByInsight,
  toInsightKey,
  toInsightKeyList,
  insightGraphRowToFraction,
  insightTrendRowToFraction,
  insightAdvancedRow,
} from 'services/insightCasino';

/** Insight search cache timeframe: 6 = 30d (months1), the widest offered. */
export const INSIGHT_SEARCH_TIMEFRAME = 6;

export interface ICheckoutResponse {
  id: string;
}

export interface ICasinos {
  status: string;
  data: Array<ICasinoSchema>;
}

export interface Reward {
  rank: number;
  amount: number;
  amountType: 'cash' | 'free play';
  badge: 'gold' | 'silver' | 'bronze' | 'numbered';
}

export interface EventData {
  subtitle: string;
  dateRange: string;
  notice: string;
  amount: number;
  label: string;
  footerNote: string;
  bonusTitle: string;
  bonusSubtitle: string;
  rewards: Reward[];
}

export interface BonusRewardItem {
  badge: string;
  badgeText: string;
  text: string;
  amount?: number;
  amountFormat?: string;
  amountSymbol?: string;
  amountType?: string;
}

export interface BonusRewardReward {
  badge: string;
  badgeText: string;
  amount?: number;
  amountFormat?: string;
  amountSymbol?: string;
  amountType?: string;
}

export interface BonusReward {
  title?: string;
  descriptionLines?: { text: string }[];
  rewards?: BonusRewardReward[];
  bonusRewarditems?: BonusRewardItem[];
}

export interface ChallengeDetails {
  subtitle?: string;
  description?: string;
  dateRange?: string;
  notice?: string;
  label?: string;
  footerNote?: string;
  bonusTitle?: string;
  bonusSubtitle?: string;
  rewards?: Reward[];
  bonusRewards?: BonusReward;
  exploreHeaderStlye?: string;
  showInsightTopPins?: number;
  pinBackgroundColor?: string;
  pinNumberColor?: string;
}

export interface ChallengeStyleData {
  name?: string;
  background?: string;
  charimg?: string;
  backgroundColor?: string;
  secondaryColor?: string;
  textColor?: string;
  btncolor?: string;
  primaryColor?: string;
  ctaBackground?: string;
  btnJoinBackground?: string;
  btnJoinColor?: string;
  alignTop?: boolean;
  headerBackground?: string;
}

export interface ChallengeTemplate {
  uuid?: string;
  name?: string;
  styleName?: string;
  challengeTrackingType?: string;
  active?: boolean;
  version?: number;
  styleData?: ChallengeStyleData;
  /** Fallback/default details — merged with instance-level details at render time */
  details?: ChallengeDetails;
  showValueInLeaderboard?: boolean;
}

export interface IChallengeItem {
  uuid?: string;
  _id?: string;
  casinoId?: number;
  name?: string;
  description?: string;
  descriptions?: {
    pending?: string;
    enrolled?: string;
    running?: string;
    complete?: string;
  };
  valueLabel?: string;
  joined?: boolean;
  active?: boolean;
  featured?: boolean;
  status?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  viewableDate?: string | Date;
  /** Instance-specific prize pool */
  winning?: number;
  /** Instance-specific customization — merged over challenge.details at render time */
  details?: ChallengeDetails;
  enrollmentType?: string;
  challengeTrackingType?: string;
  displayValue?: string;
  participantGrouping?: string;
  teamEnabled?: boolean;
  multiplier?: number;
  egms?: string;
  showValueInLeaderboard?: boolean;
  participantStatus?: string;
  /** Snapshotted challenge template at instance creation time */
  challenge?: ChallengeTemplate;
}

export interface IChallengeInstanceEGM {
  _id?: string;
  uuid?: string;
  casinoId?: number;
  challengeInstanceUUID?: string;
  machineId: string;
  pinDisplayType?: string;
  pinDisplayCode?: string;
  createdDate?: string;
  updatedDate?: string;
  active?: boolean;
}

export interface IGetChallengesResponse {
  featured: IChallengeItem[];
  others: IChallengeItem[];
  raw: unknown;
}

export interface IGetChallengeByIdResponse {
  status: string;
  Data: IChallengeItem | null;
}

export interface IRequestWithPaginationChallengeLeaderParams extends IRequestPaginationParams {
  challengeInstanceUUID: string;
  casinofk: number;
  appUserFk?: string;
  isCasinoAccount?: boolean;
}

export interface IRequestPlayDataReport {
  casinofk: number;
  timeframe: number;
  insight: string;
  slot: number;
  orderby: OrderByEnum;
}

export interface IRequestCasinoParams {
  casinofk: number;
  timeframe: number;
  insight: string;
  slot: number;
  slotnumbers: string;
  limit: number;
  pageNumber: number;
  orderby: OrderByEnum;
  compareSlotNumbers: string;
}

export interface IRequestCommonParams {
  casinofk: number;
  timeframe: number;
  insight: string;
  orderby: OrderByEnum;
}

export interface IRequestLeaderCommonParams {
  casinofk: number;
  timeframe: number;
  insight: number;
  orderby: OrderByEnum;
  ordervalue: OrderValueEnum;
}

export interface IRequestPaginationParams {
  limit: number;
  pageNumber: number;
}

export interface IRequestUserParams extends IRequestCommonParams {
  appUserFk: string;
  slot: number;
  reportType: string;
}

export interface IRequestMyJackpotParams extends IRequestCommonParams {
  playerid: string;
}

export interface IRequestAllReportParams
  extends IRequestCommonParams,
    IRequestUserParams,
    IRequestMyJackpotParams,
    IRequestPaginationParams {}

interface IRequestWithoutPaginationParams extends IRequestUserParams {}
interface IRequestWithPaginationParams extends IRequestUserParams, IRequestPaginationParams {}

interface IRequestWithoutPaginationJackpotParams extends IRequestMyJackpotParams {}
interface IRequestWithPaginationJackpotParams
  extends IRequestMyJackpotParams,
    IRequestPaginationParams {}

export interface IRequestLeaderParam extends IRequestLeaderCommonParams {
  appUserFk: string;
}

export interface IRequestWithoutPaginationLeaderParams extends IRequestLeaderParam {}

export type LeadboardMetric = 'MostSpins' | 'MostWon' | 'MostJackpot' | 'MostWonBySpin';
export type LeadboardWindow = '24h' | '7d' | '30d';

export interface IGetLeadboardsParams {
  casinoId: number;
  metric: LeadboardMetric;
  window_name: LeadboardWindow;
}

export interface IRequestWithPaginationLeaderParams
  extends IRequestLeaderParam,
    IRequestPaginationParams {}

export interface IRequestDetailParam {
  casinofk: number;
  timeframe?: number;
  insight: string;
  slot: number;
  orderby: OrderByEnum;
}

export interface IRequestTrendsParam {
  casinoFk: number;
  insightFilter: string;
  slotNumber?: number;
}

export interface IRequestAdvancedTrendsParam {
  casinoFk: number;
  insightFilter: string;
  orderby: OrderByEnum;
  orderByField: string;
  timeframe: string;
  limit: number;
  pageNumber: number;
  slotNumbers: string;
}

export interface IRequestGraphParam {
  casinofk: number;
  insight_filter: number;
  bucket_time: string;
  slot_number: number;
  timebucket_id: string;
  timeframe_filter: number;
}


export interface IPlayerEngagementRow {
  casinoId?: number;
  month: string;
  ageBand: string;
  gender: string;
  activeUniqueLinkedAccounts: number;
  activeSessions: number;
  avgLoginsPerActivePlayer: number;
  accountsCreated: number;
  isPartial?: boolean;
}

export interface IPlayerEngagementReportResponse {
  status?: string;
  DataSet: IPlayerEngagementRow[];
  meta?: {
    fromMonth: string;
    toMonth: string;
    breakout: 'none' | 'casino';
    empty: boolean;
    timezone?: string;
  };
}

export interface IRequestPlayerEngagementReport {
  casinoIds?: string;
  fromMonth: string;
  toMonth: string;
  ageBands?: string;
  genders?: string;
  breakout?: 'none' | 'casino';
  includePartialMonth?: string;
}

export interface ISlotCheckEngagementRow {
  month: string;
  ageBand: string;
  gender: string;
  activeUniqueAccounts: number;
  activeLogins: number;
  avgLoginsPerActiveAccount: number;
  accountsCreated: number;
  accountsLinked: number;
  isPartial?: boolean;
}

export interface ISlotCheckEngagementReportResponse {
  status?: string;
  DataSet: ISlotCheckEngagementRow[];
  meta?: {
    fromMonth: string;
    toMonth: string;
    empty: boolean;
    timezone?: string;
  };
}

export interface IRequestSlotCheckEngagementReport {
  fromMonth: string;
  toMonth: string;
  ageBands?: string;
  genders?: string;
  includePartialMonth?: string;
}


export interface IPlayerDevelopmentKpis {
  players: number;
  usedThisWeek: number;
  visitedThisWeek: number;
  activeUseAndVisit: number;
  newUsers: number;
  useLastMonth: number;
  useLastWeek: number;
  useMonthToDate: number;
  useThisWeek: number;
  visitsLastMonth: number;
  visitsLastWeek: number;
  visitsMonthToDate: number;
  visitsThisWeek: number;
  useWowPct: number | null;
  useMomPct: number | null;
  visitsWowPct: number | null;
  visitsMomPct: number | null;
}

export interface IPlayerDevelopmentWeekPoint {
  week: string;
  weekStart: string;
  use: number;
  visits: number;
  activeUseAndVisit: number;
  newUsers: number;
  byAge?: Record<string, { use: number; visits: number; activeUseAndVisit: number; newUsers: number }>;
  [key: string]: unknown;
}

export interface IPlayerDevelopmentMix {
  week: string;
  byAge: Array<{ ageBand: string; count: number; pct: number }>;
  byGender: Array<{ gender: string; count: number; pct: number }>;
  genderByAge: Array<{ ageBand: string; gender: string; count: number; pct: number }>;
}

export interface IPlayerDevelopmentReportResponse {
  status?: string;
  meta?: {
    casinoId: number;
    fromWeek: string;
    toWeek: string;
    fromWeekStart: string;
    toWeekStart: string;
    latestWeek: string;
    latestWeekStart: string;
    latestWeekLabel: string;
    empty: boolean;
    timezone?: string;
  };
  kpis: IPlayerDevelopmentKpis;
  weekSeries: IPlayerDevelopmentWeekPoint[];
  mix: IPlayerDevelopmentMix;
}

export interface IRequestPlayerDevelopmentReport {
  casinoIds: string;
  fromWeek: string;
  toWeek: string;
  ageBands?: string;
  genders?: string;
  includePartialWeek?: string;
}


export interface ICloakEgmRow {
  uuid: string;
  casinoId: number;
  mnum: string;
  active: boolean;
  expirationDate?: string;
  name?: string;
  theme?: string;
}

export interface IGetCloakedEgmsResponse {
  status?: string;
  casinoId?: number;
  mnums?: string[];
}

export interface IGetCloakedEgmsAdminResponse {
  status?: string;
  casinoId?: number;
  rows?: ICloakEgmRow[];
}

export interface ISystemDashboardFreshnessRow {
  casinoId: number;
  casinoName: string;
  insightInstance?: number | null;
  collection?: string;
  minUpdateDate?: string | null;
  maxUpdateDate?: string | null;
  currentDateTime?: string | null;
  ageMinutes?: number | null;
  staleLevel?: string;
  timeZoneLabel?: string;
  message?: string | null;
}

export interface ISystemDashboardWidget {
  id: string;
  title: string;
  status?: 'ok' | 'error' | string;
  error?: string;
  staleThresholds?: { amberMinutes: number; redMinutes: number };
  columns?: Array<{ key: string; label: string; shortLabel: string }>;
  rows?: ISystemDashboardFreshnessRow[];
}

export interface ISystemDashboardResponse {
  status?: string;
  serverNow?: string;
  updateDateSemantics?: string;
  timeZoneLabel?: string;
  widgets?: ISystemDashboardWidget[];
}

export interface IMappedManufacturerBreakdown {
  manufacturer: string;
  egmCount: number;
  activeCount: number;
}

export interface IManagedManufacturerRow {
  slotcheckManufacturer: string;
  egmCount: number;
  activeCount: number;
  manufacturers: string[];
  /** Per-raw counts for expand / Unmap (preferred over manufacturers[] alone). */
  mappedFrom?: IMappedManufacturerBreakdown[];
}

export interface IUnmappedManufacturerRow {
  manufacturer: string;
  egmCount: number;
  activeCount: number;
}

export interface IManufacturersManagementResponse {
  status?: string;
  managed?: IManagedManufacturerRow[];
  unmapped?: IUnmappedManufacturerRow[];
  insightInstances?: number[];
}

export interface ISetSlotcheckManufacturerResponse {
  status?: string;
  manufacturer?: string;
  slotcheckManufacturer?: string;
  matchedCount?: number;
  modifiedCount?: number;
  instancesUpdated?: number[];
}

export interface IUnsetSlotcheckManufacturerResponse {
  status?: string;
  manufacturer?: string;
  slotcheckManufacturer?: string;
  matchedCount?: number;
  modifiedCount?: number;
  instancesUpdated?: number[];
}

export interface IGameNameComboRow {
  gameName: string;
  gameTitle: string;
  /** Effective manufacturer (slotcheckManufacturer else raw) — row key / Set match. */
  manufacturer: string;
  displayManufacturer: string;
  /** Raw machine manufacturer codes folded into this row. */
  rawManufacturers?: string[];
  slotcheckGameName: string;
  slotcheckGameTitle: string;
  egmCount: number;
  activeCount: number;
  isPartial?: boolean;
}

/** SlotCheck (Game Name, Game Theme) pair + the raw combos mapped into it. */
export interface IGameNameMappingGroup {
  slotcheckGameName: string;
  slotcheckGameTitle: string;
  egmCount: number;
  activeCount: number;
  isPartial?: boolean;
  mappedFrom: IGameNameComboRow[];
}

export interface IGameNamesManagementResponse {
  status?: string;
  managed?: IGameNameComboRow[];
  /** Managed combos grouped by SlotCheck pair (paged by group). */
  managedGroups?: IGameNameMappingGroup[];
  managedGroupsTotal?: number;
  unmapped?: IGameNameComboRow[];
  managedTotal?: number;
  unmappedTotal?: number;
  /** Distinct effective manufacturers for Filter manufacturer autocomplete. */
  manufacturers?: string[];
  insightInstances?: number[];
  q?: string;
  manufacturer?: string;
  limit?: number;
  offset?: number;
}

export interface ISetSlotcheckGameNamesResponse {
  status?: string;
  gameName?: string;
  gameTitle?: string;
  manufacturer?: string;
  slotcheckGameName?: string;
  slotcheckGameTitle?: string;
  matchedCount?: number;
  modifiedCount?: number;
  instancesUpdated?: number[];
}

export interface IUnsetSlotcheckGameNamesResponse {
  status?: string;
  gameName?: string;
  gameTitle?: string;
  manufacturer?: string;
  slotcheckGameName?: string;
  slotcheckGameTitle?: string;
  matchedCount?: number;
  modifiedCount?: number;
  instancesUpdated?: number[];
}

export interface ICloakEgmWriteResponse {
  status?: string;
  row?: ICloakEgmRow;
  deleted?: boolean;
}


export type MyPlayPeriodQuery = 'today' | 'last_visit' | 'year';

export interface IMyPlayKpis {
  jackpotsCount: number;
  jackpotsValue: number;
  spins: number;
  winPerSpin: number;
  moneyWon: number;
}

export interface IMyPlayJackpotCard {
  gameName: string;
  theme: string;
  /** Machine id (insight/LOTC: mast id). Never floorLocation. */
  mnum: string;
  /** Floor location (same as slot_number). */
  floorLocation?: string | null;
  /** Alias of floorLocation (backward compat). */
  slot_number?: string | number | null;
  section: string;
  /** Insight casinos only: bank from Mongo machines. */
  bank?: string;
  jackpotValue: number;
  jackpotValueNtile: number;
  createDate: string;
  slipNumber?: string;
}

export interface IMyPlayGamesPlayedCard {
  gameName: string;
  theme: string;
  /** Machine id (insight/LOTC: mast id). Never floorLocation. */
  mnum: string;
  /** Floor location (same as slot_number). */
  floorLocation?: string | null;
  /** Alias of floorLocation (backward compat). */
  slot_number?: string | number | null;
  section: string;
  /** Insight casinos only: bank from Mongo machines. */
  bank?: string;
  spins: number;
  playDate?: string;
  spinsNtile?: number;
  moneyWon?: number;
  moneyWonNtile?: number;
  jackpotValue?: number;
  jackpotValueNtile?: number;
  moneyPlayed?: number;
  moneyPlayedNtile?: number;
  return?: number;
  returnNtile?: number;
  winPerSpin?: number;
  winPerSpinNtile?: number;
  jackpots?: number;
}

export interface IMyPlayVisits {
  total: number;
  avgPerMonth: number;
  lastVisitDate: string;
}

export interface IMyPlayMostPlayedTitles {
  totalSpins: number;
  /** Array when API joins; JSON string of mnums when Mongo stored stringified titles. */
  /** mnum is sent by the server but ignored by the donut. */
  titles: Array<{ name: string; spins: number; mnum?: string }> | string;
}

export interface IGetMyPlayEmpty {
  empty: true;
}

export interface IGetMyPlayDayBody {
  empty?: false;
  period: 'today' | 'last_visit';
  kpis: IMyPlayKpis;
  jackpot: IMyPlayJackpotCard | null;
  mostPlayed: IMyPlayGamesPlayedCard | null;
  gamesPlayed: IMyPlayGamesPlayedCard[];
  year?: number;
}

export interface IGetMyPlayYearBody {
  empty?: false;
  period: 'year';
  year: number;
  kpis: IMyPlayKpis;
  jackpot: IMyPlayJackpotCard | null;
  visits: IMyPlayVisits | null;
  mostPlayedTitles: IMyPlayMostPlayedTitles | null;
}

export type IGetMyPlayResponse = IGetMyPlayEmpty | IGetMyPlayDayBody | IGetMyPlayYearBody;

export class CasinoService extends ApiService {
  constructor() {
    super('casino');
  }

  private getCasinoIdHeader(casinoId: number) {
    return {
      headers: {
        casinoId: casinoId?.toString(),
      },
    };
  }
  GetCasinoInfo(casinoId: string) {
    return Get<ICheckoutResponse>(`${this.baseRoutePath}/GetCasinoInfo`, { casinoId }, {}, true);
  }

  GetAllCasinos() {
    return Get<IListResponse<ICasinoSchema>>(`${this.baseRoutePath}/GetAllCasinos`, {}, {}, true);
  }

  /** Shared Mongo applicationConfigurations (main SlotCheck DB). */
  GetApplicationConfigurations(params?: { name?: string; type?: string }) {
    return Get<IListResponse<import('lib/applicationConfig').ApplicationConfigRow>>(
      `${this.baseRoutePath}/GetApplicationConfigurations`,
      params || {},
      {},
      true
    );
  }

  // ---------------- casinoInsight (insightInstance) ----------------
  // Casinos whose object carries `insightInstance` are served ONLY by the
  // GetCasinoInsight* routes; the legacy report routes below are never called
  // for them (they 403). Casinos without it are untouched.

  /** Full GetCasinoInsights rows (shared cache; includes onMap:false machines). */
  GetInsightSummary(kpCasinoPk: number, timeframe?: number | string) {
    return this.insightSummary(kpCasinoPk, timeframe);
  }

  private insightSummary(casinoId: number, timeframe?: number | string) {
    const id = insightCasinoId(casinoId);
    const tf = resolveInsightTimeframe(timeframe).id;
    return sharedInsightSummary(id, tf, () =>
      Get<IInsightSummaryResponse>(
        `${this.baseRoutePath}/GetCasinoInsights`,
        { casinoId: String(id), timeframe: String(tf) },
        {},
        true
      )
    );
  }

  GetCasinoInsightGraph(params: { casinoId: number; timeframe?: number | string; slot?: number; mnum?: string; insight?: string }) {
    const query: Record<string, string> = {
      casinoId: String(insightCasinoId(params.casinoId)),
      timeframe: String(resolveInsightTimeframe(params.timeframe).id),
      // Device-local wall-clock for Hour/Day axis (Hour 1 = current local hour).
      localNowMs: String(clientLocalNowAsUtcMs()),
    };
    if (params.mnum) query.mnum = String(params.mnum);
    else if (params.slot) query.slot = String(params.slot);
    const insight = toInsightKey(params.insight);
    if (insight) query.insight = insight;
    return Get<ISlotResponse<ICasinoReport> & { bucketType?: string; buckets?: string[] }>(
      `${this.baseRoutePath}/GetCasinoInsightGraph`,
      query,
      {},
      true
    ).pipe(
      map((resp) => ({
        ...(resp as ISlotResponse<ICasinoReport>),
        // API sends return as a percent; Details expects the legacy fraction.
        DataSet: ((resp && (resp as ISlotResponse<ICasinoReport>).DataSet) || []).map(insightGraphRowToFraction),
        slotPlayData: ((resp && resp.slotPlayData) || []).map(insightGraphRowToFraction),
        totalJackpots: (resp && resp.totalJackpots) || [],
      }))
    );
  }

  GetCasinoInsightTrends(params: IRequestTrendsParam) {
    const query: Record<string, string> = { casinoId: String(insightCasinoId(params.casinoFk)) };
    const insights = toInsightKeyList(params.insightFilter);
    if (insights) query.insightFilter = insights;
    // Insight Details passes mnum as slotNumber (egmDetailId). Send as `mnum`,
    // never as `slot` — backend `slot` means machines.floorLocation and the
    // schema is ^[0-9]{1,32}$ (alphanumeric floor codes like 2A5 fail validation).
    if (params.slotNumber) query.mnum = String(params.slotNumber);
    return Get<IListResponse<ITrendsReport>>(`${this.baseRoutePath}/GetCasinoInsightTrends`, query, {}, true).pipe(
      map((resp) =>
        resp && Array.isArray(resp.DataSet)
          ? { ...resp, DataSet: resp.DataSet.map(insightTrendRowToFraction) }
          : resp
      )
    );
  }

  GetCasinoInsightAdvancedTrends(params: IRequestAdvancedTrendsParam) {
    const query: Record<string, string> = { casinoId: String(insightCasinoId(params.casinoFk)) };
    const insights = toInsightKeyList(params.insightFilter);
    if (insights) query.insightFilter = insights;
    const orderByField = toInsightKey(params.orderByField);
    if (orderByField && (!insights || insights.split(',').includes(orderByField))) {
      query.orderByField = orderByField;
    }
    if (params.timeframe) query.timeframe = String(params.timeframe);
    if (params.orderby) query.orderby = String(params.orderby).toLowerCase();
    if (params.limit) query.limit = String(params.limit);
    if (params.pageNumber !== undefined && params.pageNumber !== null) query.pageNumber = String(params.pageNumber);
    // Compare/favorites ids for insight casinos are mnums (egmDetailId), not floorLocation.
    if (params.slotNumbers && /^[0-9,]+$/.test(params.slotNumbers)) query.mnums = params.slotNumbers;
    const advanced = Get<IListResponse<IGameData>>(`${this.baseRoutePath}/GetCasinoInsightAdvancedTrends`, query, {}, true);
    // Summary rows supply section/bank for the Compare label (the API pivot
    // omits them); a summary failure just leaves the label without a bank.
    const summary = this.insightSummary(params.casinoFk, params.timeframe).pipe(catchError(() => of(null)));
    return forkJoin([advanced, summary]).pipe(
      map(([resp, sum]) => {
        if (!resp || !Array.isArray(resp.DataSet)) return resp;
        const byMnum = new Map<string, Record<string, unknown>>();
        for (const r of (sum && sum.DataSet) || []) {
          if (r && r.mnum != null) byMnum.set(String(r.mnum), r as unknown as Record<string, unknown>);
        }
        return { ...resp, DataSet: resp.DataSet.map((r) => insightAdvancedRow(r, byMnum)) };
      })
    );
  }

  private insightListReport(params: IRequestCasinoParams) {
    // UI convention: OrderByEnum.Desc = highest value first (no _order flip here).
    const insight = toInsightKey(params.insight);
    const slots = parseSlotList(params.slotnumbers);
    const limit = Number(params.limit) || 0;
    const page = Number(params.pageNumber) || 0;
    return this.insightSummary(params.casinofk, params.timeframe).pipe(
      map((resp) => {
        let rows = resp.DataSet;
        // Favorites/playlist/compare filter by egmDetailId (= mnum for insight).
        if (slots) {
          rows = rows.filter(
            (r) => slots.has(String(r.mnum ?? '')) || slots.has(String(r.slot_number ?? ''))
          );
        }
        rows = sortRowsByInsight(rows, insight, params.orderby);
        const totalCount = rows.length;
        const DataSet = asReportRows(limit > 0 ? rows.slice(page * limit, page * limit + limit) : rows);
        return listResponse(DataSet, resp.updateDate || '', totalCount);
      })
    );
  }

  ListReport(params: IRequestCasinoParams) {
    if (isInsightCasino(params.casinofk)) return this.insightListReport(params);
    // ListReport sorts on `<insight>_order`, a RANK column where 1 is the
    // highest-value EGM. That is the inverse of the UI's convention, where
    // OrderByEnum.Desc means "highest value first" (Hottest — heat filter
    // [5, 4]) and Asc means "lowest value first" (Coldest — heat filter
    // [2, 1]). So the direction has to be flipped on the way to the API,
    // otherwise the drawer list comes back in the opposite order to the
    // header, the hottest/coldest shortcuts and the map's ranked markers.
    // Note this flip is specific to ListReport/`_order`; UsersListReport &
    // friends order on the raw insight value and must NOT be flipped.
    const newParams = {
      ...params,
      orderby: params.orderby === OrderByEnum.Asc ? OrderByEnum.Desc : OrderByEnum.Asc,
    };

    return Get<IListResponse<ICasinoReport>>(
      `${this.baseRoutePath}/ListReport`,
      newParams,
      this.getCasinoIdHeader(newParams.casinofk),
      true
    );
  }

  GetAdvancedListReport(params: IRequestAdvancedTrendsParam) {
    if (isInsightCasino(params.casinoFk)) return this.GetCasinoInsightAdvancedTrends(params);
    // Even though the list is showing the highest value at top thus it is  descending,
    // this list is sorting by <insight>_order which returns 1 being the highest value
    // thus, we are switching it in the api call to make the rest of UI usage make sense
    // const newParams = { ...params, orderby: params.orderby === 'asc' ? 'desc' : 'asc' };

    return Get<IListResponse<IGameData>>(
      `${this.baseRoutePath}/GetAdvancedTrends`,
      params,
      this.getCasinoIdHeader(params.casinoFk),
      true
    );
  }

  GetPlayDataReport(params: IRequestPlayDataReport) {
    if (isInsightCasino(params.casinofk)) {
      // Map data: same shared GetCasinoInsights response as the list. onMap
      // (!2116: row has floorLocation) gates the map; FloorMap then joins
      // floorLocation ↔ feature.properties.location_string (insightMapJoin).
      const insight = toInsightKey(params.insight);
      return this.insightSummary(params.casinofk, params.timeframe).pipe(
        map((resp) => {
          const rows = sortRowsByInsight(
            // onMap = has machines.floorLocation; slot_number may be alphanumeric (e.g. 2A5).
            resp.DataSet.filter(
              (r) =>
                r.onMap === true &&
                r.slot_number !== null &&
                r.slot_number !== undefined &&
                String(r.slot_number).trim() !== ''
            ),
            insight,
            params.orderby
          );
          return listResponse(asReportRows(rows), resp.updateDate || '');
        })
      );
    }
    return Get<IListResponse<ICasinoReport>>(
      `${this.baseRoutePath}/GetPlayDataReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  GetPlayerEngagementReport(params: IRequestPlayerEngagementReport) {
    return Get<IPlayerEngagementReportResponse>(
      `${this.baseRoutePath}/GetPlayerEngagementReport`,
      params,
      {
        // Avoid Amplify/browser 304 with empty body leaving the PE page blank.
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetPlayerEngagementReportExport(params: IRequestPlayerEngagementReport) {
    return Get<Blob>(
      `${this.baseRoutePath}/GetPlayerEngagementReportExport`,
      params,
      { responseType: 'blob' },
      true
    );
  }

  GetSlotCheckEngagementReport(params: IRequestSlotCheckEngagementReport) {
    return Get<ISlotCheckEngagementReportResponse>(
      `${this.baseRoutePath}/GetSlotCheckEngagementReport`,
      params,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetSlotCheckEngagementReportExport(params: IRequestSlotCheckEngagementReport) {
    return Get<Blob>(
      `${this.baseRoutePath}/GetSlotCheckEngagementReportExport`,
      params,
      { responseType: 'blob' },
      true
    );
  }

  GetPlayerDevelopmentReport(params: IRequestPlayerDevelopmentReport) {
    return Get<IPlayerDevelopmentReportResponse>(
      `${this.baseRoutePath}/GetPlayerDevelopmentReport`,
      params,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetPlayerDevelopmentReportExport(params: IRequestPlayerDevelopmentReport) {
    return Get<Blob>(
      `${this.baseRoutePath}/GetPlayerDevelopmentReportExport`,
      params,
      { responseType: 'blob' },
      true
    );
  }




  GetIndividualSlotReport(params: IRequestDetailParam) {
    if (isInsightCasino(params.casinofk)) {
      // Reuse Explore's GetCasinoInsights cache (memory + IndexedDB) for casinoId+timeframe;
      // network only on miss. Details then picks the mnum row — never floorLocation.
      return this.insightSummary(params.casinofk, params.timeframe).pipe(
        map((resp) => {
          const row = pickInsightSummaryRow(resp.DataSet, params.slot);
          return slotResponse(asReportRows(row ? [row] : []), resp.updateDate || '');
        })
      );
    }
    return Get<ISlotResponse<ICasinoReport>>(
      `${this.baseRoutePath}/GetIndividualSlotReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  GetGraphBucketSlotReport(params: IRequestDetailParam) {
    if (isInsightCasino(params.casinofk)) {
      return this.GetCasinoInsightGraph({
        casinoId: params.casinofk,
        timeframe: params.timeframe,
        // params.slot is mnum (egmDetailId). Send as mnum so schema stays numeric
        // and backend skips floorLocation lookup via mnumsForSlot.
        mnum: String(params.slot),
        insight: params.insight,
      });
    }
    return Get<ISlotResponse<ICasinoReport>>(
      `${this.baseRoutePath}/GetGraphBucketSlotReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  // The casinoId header is what points ms-casino at this casino's Timescale
  // database (casinoIdMiddleware -> updateCasinoId). Both trends endpoints query
  // `<schema>.slot_data_trends1` / `<schema>.slot_trends_list_report`, so without
  // the header they run against whichever casino the last header-carrying
  // request left that instance pointed at — which returns no rows (or errors)
  // whenever that is a different casino. Every other casino-scoped report call
  // sends it; these two did not.
  GetTrendsData(params: IRequestTrendsParam) {
    if (isInsightCasino(params.casinoFk)) return this.GetCasinoInsightTrends(params);
    return Get<IListResponse<ITrendsReport>>(
      `${this.baseRoutePath}/GetTrendsData`,
      params,
      this.getCasinoIdHeader(params.casinoFk),
      true
    );
  }

  GetTrendsListDataReport(params: IRequestTrendsParam) {
    if (isInsightCasino(params.casinoFk)) return this.GetCasinoInsightTrends(params);
    return Get<IListResponse<ITrendsReport>>(
      `${this.baseRoutePath}/GetTrendsListDataReport`,
      params,
      this.getCasinoIdHeader(params.casinoFk),
      true
    );
  }

  GetGraphData(params: IRequestGraphParam) {
    // Bucket drill-down has no casinoInsight equivalent; GetCasinoInsightGraph
    // already returns the bars, so insight casinos never call the legacy route.
    if (isInsightCasino(params.casinofk)) return emptyList<IGraphGameData>();
    return Get<IListResponse<IGraphGameData>>(
      `${this.baseRoutePath}/GetGraphData`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  UsersListReport(params: IRequestWithPaginationParams) {
    return Get<IListResponse<ICasinoReport>>(
      `${this.baseRoutePath}/UsersListReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  MyJackpotListReport(params: IRequestWithPaginationJackpotParams) {
    return Get<IListResponse<ICasinoReport>>(
      `${this.baseRoutePath}/MyJackpotListReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  MyJackpotCountListReport(params: IRequestWithoutPaginationJackpotParams) {
    return Get<IListResponse<ICasinoReportCount>>(
      `${this.baseRoutePath}/MyJackpotCountListReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }


  GetLeadboards(params: IGetLeadboardsParams) {
    return Get<ILeaderBoardList<ILeaderBoardReport>>(
      `${this.baseRoutePath}/GetLeadboards`,
      params,
      this.getCasinoIdHeader(params.casinoId),
      true
    );
  }

  LeaderBoardListReport(params: IRequestWithPaginationLeaderParams) {
    return Get<ILeaderBoardList<ILeaderBoardReport>>(
      `${this.baseRoutePath}/LeaderBoardListReport`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  LeaderBoardChallengeListReport(params: IRequestWithPaginationChallengeLeaderParams) {
    return Get<ILeaderBoardList<ILeaderBoardChallengeReport>>(
      `${this.baseRoutePath}/LeaderBoardChallengeList`,
      params,
      this.getCasinoIdHeader(params.casinofk),
      true
    );
  }

  GetGeoJSON(casinoId: number) {
    return Get<IListResponse<FloorPlan>>(
      `${this.baseRoutePath}/GetGeoJSON`,
      { casinoId },
      this.getCasinoIdHeader(casinoId),
      true
    );
  }

  GetMapMetaData(casinoId: number) {
    // Insight casinos take placement from the GetCasinoInsights rows instead.
    if (isInsightCasino(casinoId)) return emptyList<SlotMetaData>();
    return Get<IListResponse<SlotMetaData>>(
      `${this.baseRoutePath}/GetMapMetaData`,
      { casinoId },
      this.getCasinoIdHeader(casinoId),
      true
    );
  }

  // Fetch challenges for a casino and return a normalized shape { featured: [], others: [] }
  GetChallenges(casinoId: number, isCasinoAccount = false) {
    return Get<{ featuredArray?: IChallengeItem[]; challengeArray?: IChallengeItem[] }>(
      `${this.baseRoutePath}/GetChallenges`,
      { isCasinoAccount },
      this.getCasinoIdHeader(casinoId),
      true
    ).pipe(
      map((resp): IGetChallengesResponse => {
        const response = (resp || {}) as {
          featuredArray?: IChallengeItem[];
          challengeArray?: IChallengeItem[];
        };
        const featured = Array.isArray(response.featuredArray) ? response.featuredArray : [];
        const others = Array.isArray(response.challengeArray) ? response.challengeArray : [];
        return { featured, others, raw: response };
      })
    );
  }

  GetChallengeById(casinoId: number, challengeId: string) {
    return Get<IGetChallengeByIdResponse>(
      `${this.baseRoutePath}/GetChallengeById`,
      { challengeInstanceUUID: challengeId },
      this.getCasinoIdHeader(casinoId),
      true
    ).pipe(map((resp) => resp?.Data || null));
  }

  /**
   * Mongo `machines` catalog for Search + display-id lookup (slotNumber).
   *
   * Always hits ms-casino GetSearchData — including insight casinos. Do NOT
   * short-circuit to insightSummary/GetCasinoInsights: those rows intentionally
   * omit slotNumber (#89), so building setActiveMachinesCatalog from them made
   * Explore/Compare/Details fall back to mnum (7916 instead of 16394).
   *
   * Insight values still come from GetCasinoInsights; only the mnum→slotNumber
   * display catalog comes from this machines response.
   */
  GetSearchData(casinoId: number) {
    // Insight routes use Mongo casinoId; callers pass kpCasinoPk.
    const queryCasinoId = isInsightCasino(casinoId) ? insightCasinoId(casinoId) : casinoId;
    return Get<IListResponse<ISearchInfo>>(
      `${this.baseRoutePath}/GetSearchData`,
      { casinoId: queryCasinoId },
      this.getCasinoIdHeader(casinoId),
      true
    );
  }

  UpdateRecentCasinoID(casinoId: string, appUserFk: string) {
    return Post<ICheckoutResponse>(
      `${this.baseRoutePath}/UpdateRecentCasinoID`,
      { casinoId, appUserFk },
      this.getCasinoIdHeader(parseInt(casinoId)),
      true
    );
  }

  UpdatePrimaryCasinoID(casinoId: string, appUserFk: string) {
    return Post<ICheckoutResponse>(
      `${this.baseRoutePath}/UpdatePrimaryCasinoID`,
      { casinoId, appUserFk },
      this.getCasinoIdHeader(parseInt(casinoId)),
      true
    );
  }

  GetChallengeInstanceEGMs(challengeInstanceUUID: string, casinoId: number) {
    return Get<IChallengeInstanceEGM[]>(
      `${this.baseRoutePath}/GetChallengeInstanceEGMs`,
      { challengeInstanceUUID },
      this.getCasinoIdHeader(casinoId),
      true
    );
  }

  EnrollChallengeInstance(challengeInstanceUUID: string, casinoId: number) {
    return Post<any>(
      `${this.baseRoutePath}/EnrollChallengeInstance`,
      { challengeInstanceUUID },
      this.getCasinoIdHeader(casinoId),
      true
    );
  }

  GetCloakedEgms(casinoId: string) {
    return Get<IGetCloakedEgmsResponse>(
      `${this.baseRoutePath}/GetCloakedEgms`,
      { casinoId: String(casinoId) },
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetCloakedEgmsAdmin(casinoId: string) {
    return Get<IGetCloakedEgmsAdminResponse>(
      `${this.baseRoutePath}/GetCloakedEgmsAdmin`,
      { casinoId: String(casinoId) },
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetSystemDashboard(widgets?: string[]) {
    const params: Record<string, string> = {};
    if (widgets && widgets.length) params.widgets = widgets.join(',');
    return Get<ISystemDashboardResponse>(
      `${this.baseRoutePath}/GetSystemDashboard`,
      params,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  GetManufacturersManagement() {
    return Get<IManufacturersManagementResponse>(
      `${this.baseRoutePath}/GetManufacturersManagement`,
      {},
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  SetSlotcheckManufacturer(body: {
    manufacturer: string;
    slotcheckManufacturer: string;
  }) {
    return Post<ISetSlotcheckManufacturerResponse>(
      `${this.baseRoutePath}/SetSlotcheckManufacturer`,
      body,
      {},
      true
    );
  }

  UnsetSlotcheckManufacturer(body: {
    manufacturer: string;
    slotcheckManufacturer: string;
  }) {
    return Post<IUnsetSlotcheckManufacturerResponse>(
      `${this.baseRoutePath}/UnsetSlotcheckManufacturer`,
      body,
      {},
      true
    );
  }

  GetGameNamesManagement(params?: {
    q?: string;
    manufacturer?: string;
    limit?: number;
    offset?: number;
  }) {
    const query: Record<string, string> = {};
    const q = String(params?.q || '').trim();
    const manufacturer = String(params?.manufacturer || '').trim();
    if (q) query.q = q;
    if (manufacturer) query.manufacturer = manufacturer;
    if (params?.limit != null) query.limit = String(params.limit);
    if (params?.offset != null) query.offset = String(params.offset);
    return Get<IGameNamesManagementResponse>(
      `${this.baseRoutePath}/GetGameNamesManagement`,
      query,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      true
    );
  }

  SetSlotcheckGameNames(body: {
    gameName: string;
    gameTitle: string;
    manufacturer: string;
    slotcheckGameName: string;
    slotcheckGameTitle: string;
  }) {
    return Post<ISetSlotcheckGameNamesResponse>(
      `${this.baseRoutePath}/SetSlotcheckGameNames`,
      body,
      {},
      true
    );
  }

  /** Unmap one raw combo from its current SlotCheck pair (clears both SlotCheck fields). */
  UnsetSlotcheckGameNames(body: {
    gameName: string;
    gameTitle: string;
    manufacturer: string;
    slotcheckGameName: string;
    slotcheckGameTitle: string;
  }) {
    return Post<IUnsetSlotcheckGameNamesResponse>(
      `${this.baseRoutePath}/UnsetSlotcheckGameNames`,
      body,
      {},
      true
    );
  }

  CloakEgm(body: { casinoId: string | number; mnum: string; name?: string; theme?: string }) {
    const payload: { casinoId: string | number; mnum: string; name?: string; theme?: string } = {
      casinoId: body.casinoId,
      mnum: body.mnum,
    };
    const name = body.name != null ? String(body.name).trim() : '';
    const theme = body.theme != null ? String(body.theme).trim() : '';
    if (name) payload.name = name;
    if (theme) payload.theme = theme;
    return Post<ICloakEgmWriteResponse>(`${this.baseRoutePath}/CloakEgm`, payload, {}, true);
  }

  UncloakEgm(body: { uuid: string } | { casinoId: string | number; mnum: string }) {
    return Post<ICloakEgmWriteResponse>(`${this.baseRoutePath}/UncloakEgm`, body, {}, true);
  }

  ExtendCloakEgm(body: { uuid: string } | { casinoId: string | number; mnum: string }) {
    return Post<ICloakEgmWriteResponse>(`${this.baseRoutePath}/ExtendCloakEgm`, body, {}, true);
  }

  GetMyPlay(casinoId: string | number, period: MyPlayPeriodQuery) {
    return Get<IGetMyPlayResponse>(
      `${this.baseRoutePath}/GetMyPlay`,
      { casinoId: String(casinoId), period },
      {},
      true
    );
  }

}
